import { combineReducers, createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import {
  getAlerts,
  getLatestData,
  getSensorData,
  getAlertsFromMultipleSensors,
  getLatestDataFromMultipleSensors,
  getOcpAvgs,
  getRangeData,
  saveOcpReport,
  getSummary,
} from '../../services/dataService';
import {
  updateDevice,
  deleteDevice,
  deleteDeviceArea,
  getAllDevicesFields,
  getAllDevicesIDs,
  getDeviceValues,
  saveSensorsFields,
  updateDevicesListReq,
} from 'services/devicesService';
import {
  getDistributors,
  getDistributorCustomers,
  getCustomerBuildings,
  getBuildingFloors,
  getBuildingAreas,
  getFilteredSensors,
  getStatusOptions,
} from '../../services/filterService';
import {
  createBuilding,
  updateBuilding,
  deleteBuilding,
  createArea,
  updateArea,
  deleteArea,
} from '../../services/buildingService';
import { filterDevices } from '../../helpers/filterHelpers';
import { getCurrentUser } from '../../services/usersService';
import { backdropSlice } from '../gui/backdropSlice';
import { heatmapDataSlice } from './heatmapDataSlice';
import { homePageSlice } from './homePageSlice';
import { controllersSlice } from './controllersSlice';
import { usersSlice, fetchUserBuildingsDetails } from './usersSlice';
import adminCenterReducer from '../adminCenter/adminCenterSlice';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

async function loginUser(credentials) {
  return fetch(`${apiUrl}login`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  }).then(data => data.json());
}

export const requestResetPassword = createAsyncThunk('login/request-reset-password', async (email, { rejectWithValue }) => {
  try {
    const response = await fetch(`${apiUrl}login/request-reset-password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data.error || 'Failed to request password reset');
    }

    return data;
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to request password reset');
  }
});

export const changeTempPassword = createAsyncThunk(
  'login/changeTempPassword',
  async ({ userId, tempPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${apiUrl}login/change-temp-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, tempPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.error || 'Failed to request password reset');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to request password reset');
    }
  },
);

export const resetPasswordWithToken = createAsyncThunk(
  'login/reset-password',
  async ({ token, email, newPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${apiUrl}login/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, email, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.error || 'Failed to reset password');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reset password');
    }
  },
);

export const getUserToken = createAsyncThunk('user/getUserToken', async (params, { dispatch, rejectWithValue }) => {
  try {
    const newToken = params.user
      ? await loginUser({
          user: params.user,
          pwd: params.pwd,
        })
      : null;

    // Check if temp password reset is required
    if (newToken?.requiresPasswordReset) {
      return {
        requiresPasswordReset: true,
        userId: newToken.userId,
        username: newToken.username,
        email: newToken.email,
      };
    }

    if (!newToken?.accessToken) {
      // custom error payload
      return rejectWithValue({ code: 'NO_TOKEN', message: newToken.error });
    }

    if (newToken && newToken.accessToken) {
      localStorage.setItem('token', newToken.accessToken);

      // After successful login, fetch current user data
      const userResult = await dispatch(fetchCurrentUser());

      if (userResult.type.endsWith('/fulfilled')) {
        if (userResult.payload.role === 'c') {
          dispatch(setSelectedDistributor(''));
          dispatch(setSelectedCustomer(userResult.payload.user_id));
          dispatch(setSelectedBuilding(userResult.payload.building_ids[0]));
        } else if (userResult.payload.role === 'd') {
          dispatch(setSelectedDistributor(userResult.payload.user_id));
          dispatch(setSelectedCustomer('all'));
        }

        return {
          token: newToken.accessToken,
          user: userResult.payload,
        };
      } else {
        // If user fetch failed, still return token but no user
        console.warn('Login successful but failed to fetch user data');
        return { token: newToken.accessToken };
      }
    }

    throw new Error('Login failed - no token received');
  } catch (error) {
    console.error('Login error:', error);
    // throw error;
    return rejectWithValue({
      code: error?.code || 'LOGIN_FAILED',
      message: error?.message || 'Login failed',
    });
  }
});

//UPDATED
export const fetchCurrentUser = createAsyncThunk('users/fetchCurrent', async (arg, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    // Get token from state or localStorage as fallback
    const token = state.sensors.token || localStorage.getItem('token');

    // If no token exists, don't make the request
    if (!token) {
      return rejectWithValue('No authentication token found');
    }

    const res = await getCurrentUser(token);

    // Check if the response is valid - be more specific about what constitutes a valid response
    if (!res || res === 200 || typeof res === 'string') {
      return rejectWithValue('Failed to fetch current user');
    }

    // Ensure we have a valid user object with required fields
    if (!res.user_id && !res.id) {
      return rejectWithValue('Invalid user data received');
    }

    return res;
  } catch (error) {
    console.error('Error fetching current user:', error);
    return rejectWithValue(error.message || 'Failed to fetch current user');
  }
});

//not in use
export const checkAuthentication = createAsyncThunk('auth/checkAuthentication', async (_, { dispatch, rejectWithValue }) => {
  try {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('currentUser');
    //should add root3 to the localStorage

    if (!token) {
      // Clear any stored user data if no token
      if (storedUser) {
        localStorage.removeItem('currentUser');
      }
      return rejectWithValue('No token found');
    }

    // Set token in state
    dispatch(setUserToken({ token }));

    // If we have stored user data that looks valid, use it temporarily
    if (storedUser && storedUser !== 'null' && storedUser !== '"Failed to fetch current user"') {
      try {
        const userData = JSON.parse(storedUser);
        // Only use stored data if it's a valid user object
        if (userData && typeof userData === 'object' && (userData.user_id || userData.id)) {
          dispatch(setCurrentUser(userData));
        }
      } catch (parseError) {
        console.log('Invalid stored user data, removing:', parseError);
        localStorage.removeItem('currentUser');
      }
    }

    // Then fetch fresh user data from server
    const result = await dispatch(fetchCurrentUser());
    if (result.type.endsWith('/fulfilled')) {
      return result.payload;
    } else {
      throw new Error(result.payload || 'Failed to authenticate');
    }
  } catch (error) {
    // Clear invalid data
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('persist:root3');
    return rejectWithValue('Invalid token');
  }
});

export const fetchUserDevices = createAsyncThunk('sensors/fetchIDs', async (params, { getState }) => {
  const state = getState();
  const selectedFilterObj = state.persistence.selectedFilterObject;
  const { resDevices, resUser } = await getAllDevicesIDs(
    params.devicesType || 'all',
    params.requestedParams || 'all',
    params.buildings_ids_list,
  );

  return { resDevices, resUser, selectedFilterObj };
});

export const updateDevicesList = createAsyncThunk('sensors/updateList', async (arg, {}) => {
  const res = await updateDevicesListReq();
});

// manage-sensors
export const updateDeviceValues = createAsyncThunk('devices/updateDevice', async (deviceData, { rejectWithValue }) => {
  try {
    return await updateDevice(deviceData);
  } catch (error) {
    return rejectWithValue(error.message || 'Update failed');
  }
});

// manage-sensors
export const deleteDeviceValues = createAsyncThunk('devices/deleteDevice', async deviceId => {
  const res = await deleteDevice(deviceId);
  return res;
});

// manage-sensors
export const removeDeviceArea = createAsyncThunk('devices/deleteDeviceArea', async deviceId => {
  const res = await deleteDeviceArea(deviceId);
  return res;
});

// manage-buildings - building service
export const createBuildingData = createAsyncThunk('buildings/createBuilding', async buildingData => {
  const res = await createBuilding(buildingData);
  return res;
});

// manage-buildings -building service
export const updateBuildingData = createAsyncThunk('buildings/updateBuilding', async buildingData => {
  const res = await updateBuilding(buildingData);
  return res;
});

// manage-buildings -building service
export const deleteBuildingData = createAsyncThunk('buildings/deleteBuilding', async buildingId => {
  const res = await deleteBuilding(buildingId);
  return { buildingId, ...res };
});

// manage-buildings -building service
export const createAreaData = createAsyncThunk('buildings/createArea', async areaData => {
  const res = await createArea(areaData);
  return res;
});

// manage-buildings -building service
export const updateAreaData = createAsyncThunk('buildings/updateArea', async areaData => {
  const res = await updateArea(areaData);
  return res;
});

// manage-buildings -building service
export const deleteAreaData = createAsyncThunk('buildings/deleteArea', async areaId => {
  const res = await deleteArea(areaId);
  return { areaId, ...res };
});

// sync persist and filterDevicesIds
export const updateFilteredDevicesWithSync = createAsyncThunk(
  'sensors/updateFilteredDevicesWithSync',
  async (filters, { dispatch, getState }) => {
    // Update sensors slice
    dispatch(updateFilteredDevices(filters));

    // Get the updated IDs and sync to persistence
    const state = getState();
    dispatch(setFilteredDevicesIds(state.sensors.filteredDevicesIds));

    return state.sensors.filteredDevicesIds;
  },
);

export const initializeFiltersForUser = createAsyncThunk(
  'persistence/initializeFilters',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const { currentUser } = state.sensors;
      const { selectedFilterObject, isInitialized } = state.persistence;

      // Don't initialize if already done or no user
      if (!currentUser?.role || isInitialized) {
        return { message: 'Already initialized or no user' };
      }

      console.log('🔄 Redux: Initializing filters for user', {
        role: currentUser.role,
        persistedFilters: selectedFilterObject,
      });

      // Load required data first
      await dispatch(getUsers()).unwrap();
      await dispatch(getBuildings()).unwrap();

      // Check current persisted state after data load
      const updatedState = getState();
      const currentSelected = updatedState.persistence.selectedFilterObject;

      // Only auto-select if NO persisted values exist
      const hasPersistedValues = currentSelected.distributor || currentSelected.customer || currentSelected.building;

      if (hasPersistedValues) {
        console.log('✅ Redux: Using persisted filters:', currentSelected);
        // Just fetch required dropdown data based on persisted state
        if (currentSelected.distributor) {
          await dispatch(fetchDistributorCustomers(currentSelected.distributor));
        }
      } else {
        console.log('🎯 Redux: No persisted values, applying defaults for role:', currentUser.role);

        // Apply role-based defaults ONLY when no persisted state exists
        if (currentUser.role === 'd') {
          // Distributor: select self as distributor and first customer
          dispatch(setSelectedDistributor(currentUser.user_id));
          const customersResult = await dispatch(fetchDistributorCustomers(currentUser.user_id));

          if (customersResult.payload?.options?.length > 0) {
            dispatch(setSelectedCustomer(customersResult.payload.options[0].value));
          }
        } else if (currentUser.role === 'c') {
          // Customer: select self as customer
          const customerIdStr = (currentUser.user_id || currentUser.id)?.toString();
          dispatch(setSelectedCustomer(customerIdStr));
        }
        // Super admin gets no defaults - they must choose
      }

      // Mark as initialized
      dispatch(setInitialized());

      return { success: true, role: currentUser.role };
    } catch (error) {
      console.error('❌ Redux: Filter initialization failed:', error);
      return rejectWithValue(error.message);
    }
  },
);

//not in use
export const fetchSensorValues = createAsyncThunk('sensor/fetchValues', async (params, { getState }) => {
  const state = getState();
  const res = await getDeviceValues(state.sensors.token, params.id);

  // TODO: replace measurements with enum
  return res;
});
//not in use
export const setSensorsFields = createAsyncThunk('sensor/setValues', async (params, { getState }) => {
  const state = getState();
  const res = await saveSensorsFields(state.sensors.token, params.id, params.data);

  // TODO: replace measurements with enum
  return res;
});

//not in use
export const fetchSensorsFields = createAsyncThunk('sensors/fetchFields', async (arg, { getState }) => {
  const state = getState();
  const res = await getAllDevicesFields(state.sensors.token);

  // TODO: replace measurements with enum
  return res;
});

//new
export const clearAuthData = createAsyncThunk('auth/clearAuthData', async (_, { dispatch }) => {
  dispatch(clearUserToken());
  localStorage.removeItem('token');
  localStorage.removeItem('currentUser');
});

export const fetchCurrentData = createAsyncThunk('sensors/fetchCurrentData', async params => {
  const res = await getLatestData(params.measurements, params.sensorId);

  return { id: params.sensorId, data: res };
});

export const fetchMultiCurrentData = createAsyncThunk('sensors/fetchMultiCurrentData', async params => {
  const res = await getLatestDataFromMultipleSensors(params.auth, params.measurements, params.sensorIds);

  return res;
});

export const fetchInitialData = createAsyncThunk('sensors/fetchData', async params => {
  const res = await getSensorData(params.auth, params.measurements, params.sensorId, params.timeRange);

  return { id: params.sensorId, data: res };
});

// fetch data with min-max range summary
export const fetchRangeData = createAsyncThunk('sensors/fetchRangeData', async (params, { rejectWithValue }) => {
  try {
    const res = await getRangeData(params.measurements, params.sensorId, params.timeRange, params.userTimeZone);

    if (res?.measurements === undefined || res?.measurements === null) {
      return rejectWithValue(res);
    }

    return { id: params.sensorId, data: res };
  } catch (error) {
    return rejectWithValue({ message: error.message, statusCode: error.statusCode });
  }
});

export const fetchMultiData = createAsyncThunk('sensors/fetchMultiData', async params => {
  const res = await getSensorData(params.auth, params.measurements, params.sensorIds, params.timeRange);

  return res;
});

export const fetchAlerts = createAsyncThunk('sensors/fetchAlerts', async params => {
  const res = await getAlerts(params.sensorId, params.timeRange, params.userTimeZone, params.forPage);

  return { id: params.sensorId, data: res };
});

// export const fetchMultiAlerts = createAsyncThunk('sensors/fetchMultiAlerts', async params => {
//   const res = await getAlertsFromMultipleSensors(params.auth, params.sensorIds, params.timeRange);

//   return res;
// });

export const fetchSummary = createAsyncThunk('sensors/fetchSummary', async params => {
  const res = await getSummary(params.auth, params.sensorIds, params.timeRange, params.timeJump, params.field);

  return res;
});

export const fetchOcpAvgs = createAsyncThunk('sensors/fetchOcpAvgs', async (params, { rejectWithValue }) => {
  try {
    // console.log('🔧 OccupancyAverages API call with updated format:', params);

    const { ocpDatesObj, building_id, floor_number, area_name, device_ids, userTimeZone } = params;

    const data = await getOcpAvgs(ocpDatesObj, building_id, device_ids, userTimeZone);

    return data;
  } catch (error) {
    console.error('❌ fetchOcpAvgs error:', error);
    return rejectWithValue(error.message);
  }
});

export const createOcpReport = createAsyncThunk('sensors/createOcpReport', async (reportData, { rejectWithValue }) => {
  try {
    const data = await saveOcpReport(reportData);
    return data;
  } catch (error) {
    console.error('❌ createOcpReport error:', error);
    return rejectWithValue(error.message || 'Failed to save occupancy report');
  }
});

//from filterService - dropdowns
export const fetchDistributors = createAsyncThunk('persistence/fetchDistributors', async (_, { rejectWithValue }) => {
  // console.log('fetchDistributors: Starting...');
  const result = await getDistributors();

  if (!result.success) {
    return rejectWithValue(result.error);
  }

  return result;
});

export const fetchDistributorCustomers = createAsyncThunk(
  'persistence/fetchDistributorCustomers',
  async (distId, { rejectWithValue }) => {
    // console.log('fetchDistributorCustomers: Starting for distributor', distId);
    const result = await getDistributorCustomers(distId);

    if (!result.success) {
      return rejectWithValue(result.error);
    }

    return result;
  },
);

export const fetchCustomerBuildings = createAsyncThunk(
  'persistence/fetchCustomerBuildings',
  async (customerId, { rejectWithValue }) => {
    // console.log('fetchCustomerBuildings: Starting for customer', customerId);
    const result = await getCustomerBuildings(customerId);

    if (!result.success) {
      return rejectWithValue(result.error);
    }

    return result;
  },
);

export const fetchBuildingFloors = createAsyncThunk(
  'persistence/fetchBuildingFloors',
  async (buildingId, { rejectWithValue }) => {
    // console.log('fetchBuildingFloors: Starting for building', buildingId);
    const result = await getBuildingFloors(buildingId);

    if (!result.success) {
      return rejectWithValue(result.error);
    }

    return result;
  },
);

export const fetchBuildingAreas = createAsyncThunk('persistence/fetchBuildingAreas', async (buildingIds, { rejectWithValue }) => {
  // console.log('fetchBuildingAreas: Starting for buildings', buildingIds);
  const result = await getBuildingAreas(buildingIds);

  if (!result.success) {
    return rejectWithValue(result.error);
  }

  return result;
});

export const fetchFilteredSensors = createAsyncThunk(
  'persistence/fetchFilteredSensors',
  async ({ buildingIds, filters = {} }, { rejectWithValue }) => {
    // console.log('fetchFilteredSensors: Starting for buildings', buildingIds, 'with filters', filters);
    const result = await getFilteredSensors({ buildingIds, filters });

    if (!result.success) {
      return rejectWithValue(result.error);
    }

    return result;
  },
);

// SLICES
const sensorsInitialState = {
  token: localStorage.getItem('token'),
  currentUser: JSON.parse(localStorage.getItem('currentUser') || '{}'),
  devices: [],
  filteredDevices: [],
  filteredDevicesIds: [],
  selectedDevicesIds: [],
  buildings_details: [],
  fields: {},
  fieldValues: {},
  names: {},
  statuses: {},
  selectedGraphs: ['clean_air_level_min', 'co2_ratio_min', 'comfort_level_avg'],
  data: {
    key: '',
    lastUpdate: 0,
    firstUpdate: 0,
    minMaxRangeMeasurements: {},
    ocpAvgsData: [],
    lastMeasurements: {},
    newData: 0,
    sensorName: '',
    building: '',
  },
  alerts: [],
  // selectedSensors: [],
  timeRange: { label: 'Today', value: 86400000 },
  ocpDatesObj: {
    startDate: DateTime.now().startOf('week').minus({ days: 1 }).toFormat('dd.MM.yyyy'),
    endDate: DateTime.now().toFormat('dd.MM.yyyy'),
  },
  loading: false,
  chartPopUp: undefined,
  searchString: '',
};

export const sensorsSlice = createSlice({
  name: 'sensors',
  initialState: sensorsInitialState,
  reducers: {
    clear: state => {
      state.sensorsInitialState = sensorsInitialState;
      // return sensorsInitialState;
    },
    clearSensorData: state => {
      state.data = {
        key: '',
        lastUpdate: 0,
        firstUpdate: 0,
        minMaxRangeMeasurements: {},
        ocpAvgsData: [],
        lastMeasurements: {},
        sensorName: '',
        building: '',
        newData: 0,
      };
      state.timeRange = { label: 'Today', value: 86400000 };
      state.newData = 0;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setSensorTimeRangeValue: (state, action) => {
      const NewTimeRange = {
        label: action.payload.label,
        value: action.payload.value,
      };
      state.timeRange = NewTimeRange;
    },
    setUserToken: (state, action) => {
      state.token = action.payload.token;
      localStorage.setItem('token', action.payload.token);

      //if token, get user and save to local
      if (action.payload.user) {
        state.currentUser = action.payload.user;
        localStorage.setItem('currentUser', JSON.stringify(action.payload.user));
      }
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
      // Save user to localStorage
      localStorage.setItem('currentUser', JSON.stringify(action.payload));
    },
    clearUserToken: state => {
      state.token = null;
      state.currentUser = {};
      // Remove from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('currentUser');
    },
    setChartPopUp: (state, action) => {
      state.chartPopUp = action.payload;
    },
    toggleGraphs: (state, action) => {
      const graphNames = action.payload;
      state.selectedGraphs = graphNames;
      // if (state.selectedGraphs.includes(graphName)) {
      //   state.selectedGraphs = state.selectedGraphs.filter(
      //     i => i !== graphName,
      //   );
      // } else {
      //   state.selectedGraphs.push(graphName);
      // }
    },

    setOcpAvgsDatesObj: (state, action) => {
      state.ocpDatesObj = action.payload;
    },
    setSelectedSensors: (state, action) => {
      state.selectedSensors = action.payload;
    },

    setSelectedDevicesIds: (state, action) => {
      state.selectedDevicesIds = (Array.isArray(action.payload) ? action.payload : [])
        .map(device => (typeof device === 'object' ? device?.id : device))
        .filter(id => id !== null && id !== undefined);
    },

    updateFilteredDevices: (state, action) => {
      const filters = action.payload; // 'selectedFilterObject' from persist
      //make sure no cust_id
      let processedFilters = { ...filters };
      if (!processedFilters.searchString) processedFilters.searchString = '';
      // if (processedFilters.customer) {
      //   delete processedFilters.customer;
      // }

      const allDevices = JSON.parse(JSON.stringify(state.devices));
      const currentUser = JSON.parse(JSON.stringify(state.currentUser));
      // const allDevices = state.filteredDevices;
      const filtered = filterDevices(allDevices, processedFilters, currentUser);
      state.filteredDevices = filtered;
      state.filteredDevicesIds = filtered.map(device => device.id);
    },

    setBuildingsDetails: (state, action) => {
      state.buildings_details = action.payload;
    },
    setDevicesList: (state, action) => {
      state.devices = action.payload;
    },
  },

  extraReducers: {
    [getUserToken.fulfilled]: (state, action) => {
      state.token = action.payload.token;
      localStorage.setItem('token', action.payload.token);

      if (action.payload.user) {
        state.currentUser = action.payload.user;
        localStorage.setItem('currentUser', JSON.stringify(action.payload.user));
        //redirect
        window.location.href = '/home';
      }
    },
    [requestResetPassword.pending]: (state, action) => {
      state.loading = true;
    },
    [requestResetPassword.fulfilled]: (state, action) => {
      state.loading = false;
      // Success - the API always returns 200 to prevent user enumeration
    },
    [requestResetPassword.rejected]: (state, action) => {
      state.loading = false;
      console.error('Password reset request failed:', action.payload);
    },
    [resetPasswordWithToken.pending]: (state, action) => {
      state.loading = true;
    },
    [resetPasswordWithToken.fulfilled]: (state, action) => {
      state.loading = false;
      // redirect to login
      window.location.href = '/authentication/sign-in';
    },
    [resetPasswordWithToken.rejected]: (state, action) => {
      state.loading = false;
      console.error('Password reset failed:', action.payload);
    },
    [checkAuthentication.fulfilled]: (state, action) => {
      state.currentUser = action.payload;
      state.loading = false;
      // Save user to localStorage
      localStorage.setItem('currentUser', JSON.stringify(action.payload));
    },
    [checkAuthentication.rejected]: (state, action) => {
      state.currentUser = {};
      state.token = null;
      state.loading = false;
      // Clear both token and user from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('currentUser');
      console.log('Authentication failed:', action.payload);
    },
    // Add reducers for the async actions in the slice
    [fetchUserDevices.pending]: state => {
      state.loading = true;
    },
    [fetchUserDevices.fulfilled]: (state, action) => {
      state.names = action.payload.resDevices?.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      state.statuses = action.payload.resDevices?.reduce((acc, item) => {
        acc[item.id] = item.status;
        return acc;
      }, {});
      const currentUser = JSON.parse(JSON.stringify(state.currentUser));

      state.devices = action.payload.resDevices;
      state.filteredDevices = filterDevices(action.payload.resDevices, action.payload.selectedFilterObj, currentUser);

      // state.filteredDevices = action.payload.resDevices;

      // if filtered devived empty filter by selection
      // if (state.filteredDevices.length === 0) {
      //   // Get current filter state from persistence
      //   const temp = state.selectedFilterObject;
      //   console.log(`selectedFilterObject: ${temp}`);
      //   const currentFilters = state.selectedFilterObject || {
      //     customer: 'all',
      //     building: 'all',
      //     floor: 'all',
      //     area: 'all',
      //     status: 'all',
      //   };

      //   // Apply filters using the existing helper function
      //   state.filteredDevices = filterDevices(state.devices, currentFilters);
      // state.filteredDevices = filterDevices(state.devices, currentFilters);
      state.filteredDevicesIds = state.filteredDevices.map(device => device.id);
      state.loading = false;
      // }
    },
    [fetchUserDevices.rejected]: state => {
      state.loading = false;
    },
    [fetchCurrentUser.pending]: state => {
      state.loading = true;
    },
    [fetchCurrentUser.fulfilled]: (state, action) => {
      // Only save if we have valid user data
      if (action.payload && typeof action.payload === 'object' && !action.payload.error) {
        state.currentUser = action.payload;
        state.loading = false;
        // Save user to localStorage when successfully fetched
        localStorage.setItem('currentUser', JSON.stringify(action.payload));
      }
    },
    [fetchCurrentUser.rejected]: (state, action) => {
      state.currentUser = {};
      state.loading = false;
      console.log('Failed to fetch current user:', action.payload);

      localStorage.removeItem('currentUser');

      // Only clear token for specific authentication errors, not general failures
      if (
        action.payload?.includes('No authentication token found') ||
        action.payload?.includes('Invalid token') ||
        action.payload?.includes('Token expired')
      ) {
        state.token = null;
        localStorage.removeItem('token');
      }
    },
    [fetchSensorValues.fulfilled]: (state, action) => {
      state.fieldValues = action.payload;
    },
    [fetchSensorsFields.fulfilled]: (state, action) => {
      state.fields = action.payload;
    },
    [fetchInitialData.fulfilled]: (state, action) => {
      const newState = structuredClone(current(state.data));

      let index = state.data.findIndex(item => item.key === action.payload.id);

      try {
        if (index === -1) {
          index =
            newState.push({
              key: action.payload.id,
              log: {
                measurements: action.payload.data,
                lastUpdate: new Date(),
              },
            }) - 1;
        } else {
          newState[index].log = {
            measurements: action.payload.data,
            lastUpdate: new Date(),
          };
        }

        state.data = newState;

        if (didMapChange) {
        }
      } catch (err) {
        console.log(err);
      }
    },
    [fetchMultiData.fulfilled]: (state, action) => {
      let didMapChange = false;
      const newState = structuredClone(current(state.data));

      try {
        action.payload?.forEach(value => {
          let index = state.data.findIndex(item => item.key === value.id);

          if (index === -1) {
            index =
              newState.push({
                key: action.payload.id,
                log: {
                  measurements: action.payload.data,
                  lastUpdate: new Date(),
                },
              }) - 1;

            newMap[value.id] = index;
            didMapChange = true;
          } else {
            newState[index].log = {
              measurements: action.payload.data,
              lastUpdate: new Date(),
            };
          }
        });
        state.data = newState;
      } catch (err) {
        console.log(err);
      }
    },
    [fetchCurrentData.fulfilled]: (state, action) => {
      let newState_data = structuredClone(current(state.data));
      try {
        newState_data = {
          key: action.payload.id,
          minMaxRangeMeasurements: newState_data.minMaxRangeMeasurements ? newState_data.minMaxRangeMeasurements : {},
          lastMeasurements: action.payload.data.measurements[0] || [],
          lastUpdate: action.payload.data.lastUpdate,
          firstUpdate: action.payload.data.firstUpdate,
          ocpAvgsData: [],
          sensorName: newState_data.sensorName ? newState_data.sensorName : '',
        };
        state.data = newState_data;
      } catch (err) {
        console.log(err);
      }
    },
    [fetchRangeData.pending]: (state, action) => {
      state.data.minMaxRangeMeasurements = {};
      state.loading = true;
    },
    [fetchRangeData.rejected]: (state, action) => {
      state.data.minMaxRangeMeasurements = {};
      if (action.payload?.lastUpdate !== undefined) {
        state.data.lastUpdate = action.payload.lastUpdate;
      }
      if (action.payload?.sensorName !== undefined) {
        state.data.sensorName = action.payload.sensorName;
      }
      if (action.payload?.building !== undefined) {
        state.data.building = action.payload.building;
      }
      state.loading = false;
    },
    [fetchRangeData.fulfilled]: (state, action, navigate) => {
      let newState_data = structuredClone(current(state.data));
      state.loading = false;
      if (!action.payload) {
        return;
      }

      try {
        newState_data = {
          key: action.payload.id,
          lastMeasurements: newState_data.lastMeasurements ? newState_data.lastMeasurements : {},
          minMaxRangeMeasurements: action.payload.data.measurements,
          lastUpdate: action.payload.data.lastUpdate,
          firstUpdate: action.payload.data.firstUpdate,
          ocpAvgsData: [],
          sensorName: action.payload.data.sensorName,
          building: action.payload.data.building,
        };

        state.data = newState_data;
        state.loading = false;
        // state.newData++;
      } catch (err) {
        console.log(err);
      }
    },
    [fetchMultiCurrentData.fulfilled]: (state, action) => {
      const newState = structuredClone(current(state.data));
      let didMapChange = false;

      // TODO: maybe handle undefined values here? a.k.a replace sensor status numeric value with the sensor status enum
      try {
        action.payload?.forEach(value => {
          let index = current(state.data).findIndex(item => item.key === value.id);
          // if sensor is new in data
          if (index === -1) {
            index =
              newState.push({
                key: value.id,
                current: {
                  measurements: value.data,
                },
              }) - 1;

            newMap[value.id] = index;
            didMapChange = true;
          } else {
            newState[index].current.measurements = value.data;
          }
        });

        if (didMapChange) {
        }
        state.data = newState;
      } catch (err) {
        console.log(err);
      }
    },
    // [fetchMultiAlerts.fulfilled]: (state, action) => {
    //   const newState = structuredClone(current(state.alerts));

    //   try {
    //     action.payload?.forEach(value => {
    //       newState[value.id] = value.data;
    //     });

    //     state.alerts = newState;
    //   } catch (err) {
    //     console.log(err);
    //   }
    // },
    [fetchAlerts.fulfilled]: (state, action) => {
      const newState = structuredClone(current(state.alerts));

      try {
        newState[action.payload.id] = action.payload.data;

        state.alerts = action.payload.data;

        // state.alerts = newState[action.payload.id].reduce(
        //   (acc, { event_type, unix_time }) => {
        //     if (!acc[event_type]) {
        //       acc[event_type] = [];
        //     }
        //     acc[event_type].push(unix_time);
        //     return acc;
        //   },
        //   {},
        // );

        // console.log(state.alerts);

        // state.alerts = newState;
      } catch (err) {
        console.log(err);
      }
      // state.loading = false;
    },
    [fetchSummary.fulfilled]: (state, action) => {
      try {
        state.summary = action.payload;
      } catch (err) {
        console.log(err);
      }
    },
    [fetchOcpAvgs.fulfilled]: (state, action) => {
      let newState_data = structuredClone(current(state.data));
      let didMapChange = false;
      let sensorIdInStore;
      if (!action.payload) {
        return;
      }
      if (state.data.key === action.payload.id) {
        // first time fetching sensor min max data
        sensorIdInStore = true;
      } else {
        sensorIdInStore = false;
      }
      try {
        newState_data = {
          lastMeasurements: newState_data.lastMeasurements ? newState_data.lastMeasurements : {},
          key: newState_data.key,
          minMaxRangeMeasurements: newState_data.minMaxRangeMeasurements,
          lastUpdate: state.data.lastUpdate,
          firstUpdate: state.data.firstUpdate,
          newData: state.data.newData + 1,
          ocpAvgsData: action.payload,
        };
        // console.log("ocpAvgsData state updated");

        state.data = newState_data;
        // state.loading = false;
      } catch (err) {
        console.log(err);
      }
    },
    [fetchUserBuildingsDetails.pending]: state => {
      state.loading = true;
    },
    [fetchUserBuildingsDetails.fulfilled]: (state, action) => {
      state.buildings_details = action.payload;
      state.loading = false;
    },
    [fetchUserBuildingsDetails.rejected]: (state, action) => {
      state.buildings_details = [];
      state.loading = false;
      console.error('Failed to fetch building details:', action.payload);
    },
  },
});

const persistenceInitialState = {
  // Original - gets persisted to localStorage
  selectedFilterObject: {
    distributor: '',
    customer: '',
    floor: 'all',
    building: 'all',
    area: 'all',
    hvacEquipment: 'all',
    status: 'active',
    searchString: '',
  },
  pdfFile: '', //legacy not used

  isInitialized: false, //for smart fetch
  shouldUpdateDevices: false, //for auto update devices
  shouldUpdateControllers: false, //for auto update devices
  filteredDevicesIds: [],
  navigation: {
    currentPage: null,
    previousPage: null,
  },

  // Store formatted dropdown options
  // options obj - gets persisted to localStorage
  options: {
    distributors: [],
    customers: [], //[{ value: 'all', label: 'All Customers' }],
    buildings: [], //[{ value: 'all', label: 'All Buildings' }],
    floors: [{ value: 'all', label: 'All Floors' }],
    areas: [{ value: 'all', label: 'All Areas' }],
    statuses: getStatusOptions(), // Static options from service
  },

  // // Store current sensors data (gets persisted to localStorage)
  // sensors: [],
  // sensorsCount: {
  //   total: 0,
  //   active: 0,
  // },

  // Simple loading state (NOT persisted - resets on page refresh)
  loading: {
    distributors: false,
    customers: false,
    buildings: false,
    floors: false,
    areas: false,
    sensors: false,
  },

  // Last error message (NOT persisted)
  lastError: null,
};

// updated
const persistenceSlice = createSlice({
  name: 'persistence',
  initialState: persistenceInitialState,
  reducers: {
    // Modern action names (cleaner than setDist, setCust, etc.)
    setSelectedDistributor: (state, action) => {
      try {
        if (!state.selectedFilterObject) {
          state.selectedFilterObject = {
            distributor: '',
            customer: '',
            building: '',
            floor: 'all',
            area: 'all',
            hvacEquipment: 'all',
            status: 'all',
            searchString: '',
          };
        }

        state.selectedFilterObject.distributor = action.payload;
        state.selectedFilterObject.customer = 'all';
        state.selectedFilterObject.building = 'all';
        state.selectedFilterObject.floor = 'all';
        state.selectedFilterObject.area = 'all';
        state.selectedFilterObject.hvacEquipment = 'all';
        state.selectedFilterObject.searchString = '';

        // Reset options (with safety checks)
        if (!state.options) state.options = {};
        state.options.customers = []; //[{ value: 'all', label: 'All Customers' }];
        state.options.buildings = []; //[{ value: 'all', label: 'All Buildings' }];
        state.options.floors = [{ value: 'all', label: 'All Floors' }];
        state.options.areas = [{ value: 'all', label: 'All Areas' }];

        state.shouldUpdateDevices = true;
      } catch (error) {
        console.error('Error in setSelectedDistributor:', error);
      }
    },

    setSelectedCustomer: (state, action) => {
      const previousCustomer = state.selectedFilterObject.customer;
      state.selectedFilterObject.customer = action.payload;

      if (previousCustomer !== action.payload) {
        // Add cascade reset logic:
        state.selectedFilterObject.building = 'all';
        state.selectedFilterObject.floor = 'all';
        state.selectedFilterObject.area = 'all';
        state.selectedFilterObject.hvacEquipment = 'all';
        state.selectedFilterObject.searchString = '';

        // Clear dependent options:
        state.options.buildings = []; //[{ value: 'all', label: 'All Buildings' }];
        state.options.floors = [{ value: 'all', label: 'All Floors' }];
        state.options.areas = [{ value: 'all', label: 'All Areas' }];

        state.shouldUpdateDevices = true;
      }
    },

    setSelectedBuilding: (state, action) => {
      state.selectedFilterObject.building = action.payload;
      // Add cascade reset logic:
      state.selectedFilterObject.floor = 'all';
      state.selectedFilterObject.area = 'all';
      state.selectedFilterObject.hvacEquipment = 'all';
      state.selectedFilterObject.searchString = '';

      // Clear dependent options:
      state.options.floors = [{ value: 'all', label: 'All Floors' }];
      state.options.areas = [{ value: 'all', label: 'All Areas' }];

      state.shouldUpdateDevices = true;
    },

    setSelectedFloor: (state, action) => {
      state.selectedFilterObject.floor = action.payload;
      // Add cascade reset logic:
      state.selectedFilterObject.area = 'all';
      state.selectedFilterObject.searchString = '';

      // Clear dependent options:
      state.options.areas = [{ value: 'all', label: 'All Areas' }];

      state.shouldUpdateDevices = true;
    },

    setSelectedArea: (state, action) => {
      state.selectedFilterObject.area = action.payload;
      state.selectedFilterObject.searchString = '';

      state.shouldUpdateDevices = true;
    },

    setSelectedHvacEquipment: (state, action) => {
      state.selectedFilterObject.hvacEquipment = action.payload;
      state.selectedFilterObject.searchString = '';
      state.shouldUpdateDevices = true;
    },

    setSelectedStatus: (state, action) => {
      state.selectedFilterObject.status = action.payload;
      state.selectedFilterObject.searchString = '';

      state.shouldUpdateDevices = true;
    },

    // Store dropdown options
    setDistributorOptions: (state, action) => {
      state.options.distributors = action.payload;
    },

    setCustomerOptions: (state, action) => {
      state.options.customers = action.payload;
    },

    setBuildingOptions: (state, action) => {
      state.options.buildings = action.payload;
    },

    setFloorOptions: (state, action) => {
      state.options.floors = action.payload;
    },

    setAreaOptions: (state, action) => {
      state.options.areas = action.payload;
    },

    setSearchString: (state, action) => {
      state.selectedFilterObject.searchString = action.payload;
      state.shouldUpdateDevices = true;
    },

    // Utility actions
    setFilterLoading: (state, action) => {
      const { type, loading } = action.payload;
      if (state.loading[type] !== undefined) {
        state.loading[type] = loading;
      }
    },

    setCurrentPage: (state, action) => {
      try {
        state.navigation.previousPage = state.navigation.currentPage;
        state.navigation.currentPage = action.payload;
      } catch (error) {
        console.error('Error in setCurrentPage:', error);
      }
    },

    setInitialized: state => {
      try {
        state.isInitialized = true;
      } catch (error) {
        console.error('Error in setInitialized:', error);
      }
    },

    setFilteredDevicesIds: (state, action) => {
      state.filteredDevicesIds = action.payload;
    },

    clearFilters: state => {
      try {
        state.selectedFilterObject = {
          distributor: '',
          customer: '',
          building: '',
          floor: 'all',
          area: 'all',
          hvacEquipment: 'all',
          status: 'all',
          searchString: '',
        };
        state.selectedFilterObject.searchString = '';
      } catch (error) {
        console.error('Error in clearFilters:', error);
      }
    },

    clearSearchString: state => {
      try {
        state.selectedFilterObject.searchString = '';
      } catch (error) {
        console.error('Error in clearSearchString:', error);
      }
    },

    clearDeviceUpdateFlag: state => {
      state.shouldUpdateDevices = false;
    },

    setError: (state, action) => {
      state.lastError = action.payload;
    },

    setPdf: (state, action) => {
      state.pdfFile = action.payload.pdfFile;
    },
  },

  extraReducers: {
    [initializeFiltersForUser.pending]: state => {
      state.loading.initialization = true;
    },
    [initializeFiltersForUser.fulfilled]: (state, action) => {
      state.loading.initialization = false;
    },
    [initializeFiltersForUser.rejected]: (state, action) => {
      state.loading.initialization = false;
    },
    // Distributors
    [fetchDistributors.pending]: state => {
      state.loading.distributors = true;
      state.lastError = null;
    },
    [fetchDistributors.fulfilled]: (state, action) => {
      state.loading.distributors = false;
      state.options.distributors = action.payload.options;
      // console.log('✅ persistenceSlice: Distributors loaded -', action.payload.options.length);
    },
    [fetchDistributors.rejected]: (state, action) => {
      state.loading.distributors = false;
      state.lastError = `Failed to load distributors: ${action.payload}`;
      console.error('❌ persistenceSlice: Distributors failed -', action.payload);
    },

    // Customers
    [fetchDistributorCustomers.pending]: state => {
      state.loading.customers = true;
      state.lastError = null;
    },
    [fetchDistributorCustomers.fulfilled]: (state, action) => {
      state.loading.customers = false;
      state.options.customers = action.payload.options;
      // console.log('✅ persistenceSlice: Customers loaded -', action.payload.options.length);
    },
    [fetchDistributorCustomers.rejected]: (state, action) => {
      state.loading.customers = false;
      state.lastError = `Failed to load customers: ${action.payload}`;
      state.options.customers = []; //[{ value: 'all', label: 'All Customers' }]; // Reset on error
      console.error('❌ persistenceSlice: Customers failed -', action.payload);
    },

    // Buildings
    [fetchCustomerBuildings.pending]: state => {
      state.loading.buildings = true;
      state.lastError = null;
    },
    [fetchCustomerBuildings.fulfilled]: (state, action) => {
      state.loading.buildings = false;
      state.options.buildings = action.payload.options;
      // console.log('✅ persistenceSlice: Buildings loaded -', action.payload.options.length);
    },
    [fetchCustomerBuildings.rejected]: (state, action) => {
      state.loading.buildings = false;
      state.lastError = `Failed to load buildings: ${action.payload}`;
      state.options.buildings = []; //[{ value: 'all', label: 'All Buildings' }]; // Reset on error
      console.error('❌ persistenceSlice: Buildings failed -', action.payload);
    },

    // Floors
    [fetchBuildingFloors.pending]: state => {
      state.loading.floors = true;
      state.lastError = null;
    },
    [fetchBuildingFloors.fulfilled]: (state, action) => {
      state.loading.floors = false;
      state.options.floors = action.payload.options;
      // console.log('✅ persistenceSlice: Floors loaded -', action.payload.options.length);
    },
    [fetchBuildingFloors.rejected]: (state, action) => {
      state.loading.floors = false;
      state.lastError = `Failed to load floors: ${action.payload}`;
      state.options.floors = [{ value: 'all', label: 'All Floors' }]; // Reset on error
      console.error('❌ persistenceSlice: Floors failed -', action.payload);
    },

    // Areas
    [fetchBuildingAreas.pending]: state => {
      state.loading.areas = true;
      state.lastError = null;
    },
    [fetchBuildingAreas.fulfilled]: (state, action) => {
      state.loading.areas = false;
      state.options.areas = action.payload.options;
      // console.log('✅ persistenceSlice: Areas loaded -', action.payload.options.length);
    },
    [fetchBuildingAreas.rejected]: (state, action) => {
      state.loading.areas = false;
      state.lastError = `Failed to load areas: ${action.payload}`;
      state.options.areas = [{ value: 'all', label: 'All Areas' }]; // Reset on error
      console.error('❌ persistenceSlice: Areas failed -', action.payload);
    },

    // Sensors
    [fetchFilteredSensors.pending]: state => {
      state.loading.sensors = true;
      state.lastError = null;
    },
    [fetchFilteredSensors.fulfilled]: (state, action) => {
      state.loading.sensors = false;
      state.sensors = action.payload.sensors;
      state.sensorsCount = action.payload.count;
      // console.log('✅ persistenceSlice: Sensors loaded -', action.payload.count.total, 'total,', action.payload.count.active, 'active');
    },
    [fetchFilteredSensors.rejected]: (state, action) => {
      state.loading.sensors = false;
      state.lastError = `Failed to load sensors: ${action.payload}`;
      state.sensors = [];
      state.sensorsCount = { total: 0, active: 0 };
      console.error('❌ persistenceSlice: Sensors failed -', action.payload);
    },
  },
});

export const {
  // Legacy actions (keep for backward compatibility)
  clear,
  setDist, //setSelectedDistributor
  setCust, //setSelectedCustomer
  setBuilding, //setSelectedBuilding
  setFlo, //setSelectedFloor
  setArea, //setSelectedArea
  setStatus, //setSelectedStatus
  setPdf,

  // Modern actions
  setSelectedDistributor,
  setSelectedCustomer,
  setSelectedBuilding,
  setSelectedFloor,
  setSelectedArea,
  setSelectedHvacEquipment,
  setSelectedStatus,
  setSearchString,

  // Option setters
  setDistributorOptions,
  setCustomerOptions,
  setBuildingOptions,
  setFloorOptions,
  setAreaOptions,

  // Data setters
  setSensors,

  // Utility actions
  setFilterLoading,
  setError,
  setCurrentPage,
  setInitialized,
  clearFilters,
  clearSearchString,
  clearDeviceUpdateFlag,
  setFilteredDevicesIds,
} = persistenceSlice.actions;

export const {
  clear: clearSensors,
  clearSensorData,
  setSensorTimeRangeValue,
  setUserToken,
  setCurrentUser,
  clearUserToken,
  setBuildingsDetails,
  setDevicesList,
  setLoading,
  setChartPopUp,
  toggleGraphs,
  setOcpAvgsDatesObj,
  setSelectedSensors,
  updateFilteredDevices,
  setSelectedDevicesIds,
} = sensorsSlice.actions;

export const { clear: clearUsers } = usersSlice.actions;
export {
  fetchUsersDetails,
  fetchUserBuildingsDetails,
  getUsers,
  getBuildings,
  createUserData,
  updateUserData,
  deleteUserData,
  updateUserPermissions,
  changeUserPassword,
  removeUserPermissions,
} from './usersSlice';

export {
  clearControllerData,
  updateFilteredControllers,
  setControllerTimeRangeValue,
  setControllerLoading,
  setControllerChartPopUp,
  toggleControllerGraphs,
  setControllerSearchString,
  setControllerOcpAvgsDatesObj,
  setSelectedControllers,
  setControllerStatus,
} from './controllersSlice';

export { fetchAllDevicesLastRecordTimes, clearDevicesLastRecordTimes } from '../adminCenter/adminCenterSlice';

// Create the root reducer
export const rootReducer = combineReducers({
  sensors: sensorsSlice.reducer,
  controllers: controllersSlice.reducer,
  heatmapData: heatmapDataSlice.reducer,
  persistence: persistenceSlice.reducer,
  users: usersSlice.reducer,
  homePage: homePageSlice.reducer,
  backdrop: backdropSlice.reducer,
  adminCenter: adminCenterReducer,
});

export const { showBackdrop, hideBackdrop } = backdropSlice.actions;

export default backdropSlice.reducer;
